// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';

// Ionic
import { AlertController, IonButton, ModalController } from '@ionic/angular/standalone';

// Local library
import { CommonFacade, PermissionService, SessionService } from '@amwager/data-access';
import {
  ConvertToRangePipe,
  GetRunnerPipe,
  GetTrackTypePipe,
  NumberWithCommasPipe,
  RaceResultPipe,
  StringToArraryPipe
} from '@amwager/util';
import {
  GUEST,
  IGetEventsDetailsResponse,
  IPoolTotalDataModel,
  IRaceResult,
  IToteResultResponse
} from '@amwager/util-ts';
import { IPersonalStatistic, ISelectedRace, ISelectedRaceRaceResult, WagerFacade } from '@amwager/wager/data-access';
import { TicketFacade } from '@amwager/ticket/data-access';

// Third party
import { Subscription, firstValueFrom } from 'rxjs';
// eslint-disable-next-line @nx/enforce-module-boundaries
import * as _ from 'lodash';
import { AuthFacade } from '@amwager/authentication/data-access';



@Component({
  selector: 'ui-ion-race-final',
  templateUrl: './race-final.component.html',
  styleUrls: ['./race-final.component.scss'],
  standalone: true,
  // changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ConvertToRangePipe,
    RaceResultPipe,
    GetTrackTypePipe,
    NumberWithCommasPipe,
    StringToArraryPipe,
    GetRunnerPipe,
    IonButton,
  ]
})
export class RaceFinalComponent implements OnInit, OnChanges {
  @Input() raceId?: number;
  @Input() eventId?: string;
  @Input() runId?: number;
  // @Input() getPoolTotals!: boolean;
  // @Input() wagerFacade!: WagerFacade;
  @Input() cdRef!: ChangeDetectorRef;
  @Input() permissionService!: PermissionService;
  @Input() router!: Router;
  @Input() selectedEvent!: IGetEventsDetailsResponse;
  // @Input() selectedRace!: ISelectedRace;

  @ViewChild('raceFinalResultsRef') raceFinalResultsRef!: ElementRef;

  private getIsConnectedInternetSubs?: Subscription;

  previousRaceId?: number;
  previousEventId?: string;

  raceResults?: IRaceResult | null;
  poolsTotal?: IPoolTotalDataModel[];
  totalHandle?: number;
  personalStatistic?: IPersonalStatistic;

  isFetchingData?: boolean = undefined;

  setResultsScrollTimeout: any;
  getRaceResultSubs?: Subscription;
  hideSection = false;
  hideSectionTimeout: any;

  private showAlertInfoHandler?: HTMLIonAlertElement;

  selectedRace?: ISelectedRace;

  constructor(
    private readonly ticketFacade: TicketFacade,
    private readonly commonFacade: CommonFacade,
    private renderer: Renderer2,
    private readonly modalCtrl: ModalController,
    private readonly alertCtrl: AlertController,
    private readonly sessionService: SessionService,
    private readonly authFacade: AuthFacade,
    private readonly wagerFacade: WagerFacade,
  ) {
    this.authFacade.isLogoutSuccess$()?.subscribe((isLogout?: boolean) => {
      if (isLogout) {
        this.alertCtrl?.dismiss();
        this.showAlertInfoHandler?.dismiss();
      }
    });
  }

  async ngOnChanges(): Promise<void> {
    this.hasPermission();

    if (!this.wagerFacade.getRaceResult$()) return;

    this.selectedRace = await firstValueFrom(this.wagerFacade?.getSelectedRace$());

    this.eventId = this.selectedRace?.eventId;
    this.raceId = this.selectedRace?.RaceId;
    this.runId = Number(this.selectedRace?.runId);

    if ((this.raceId === this.previousRaceId) && (this.eventId === this.previousEventId)) return;

    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('RaceFinalComponent:ngOnChanges', false);
      this.hideSection = true;
      return;
    }
    else this.hideSection = false;


    this.isFetchingData = true;
    this.raceResults = null;

    this.results();

    this.previousRaceId = this.raceId;
    this.previousEventId = this.eventId;

    // hide/show UI
    if (this.selectedRace?.action === 'updateRaceFinal') {
      // console.log('FINAL:ngOnChanges:updateRaceFinal:');

      this.hideSection = true;

      if (this.hideSectionTimeout) clearTimeout(this.hideSectionTimeout);
      this.hideSectionTimeout = setTimeout(() => {
        this.hideSection = false;
      }, 500);
    }

    this.cdRef.markForCheck();

  }

  async ngOnInit() {
    this.getIsConnectedInternetSubs = this.commonFacade.getIsConnectedInternet$()
      .subscribe(async (isConnectedInternet) => {
        if (isConnectedInternet?.isConnected) {
          this.hasPermission();

          this.isFetchingData = undefined;

          if (this.hideSection) {
            this.hideSection = false;
            this.raceResults = undefined;
          }

          if (!this.raceResults) {
            this.isFetchingData = true;

            this.results();

            this.selectedRace = await firstValueFrom(this.wagerFacade?.getSelectedRace$());

            this.eventId = this.selectedRace?.eventId;
            this.raceId = this.selectedRace?.RaceId;
            this.runId = Number(this.selectedRace?.runId);

            this.previousRaceId = this.raceId;
            this.previousEventId = this.eventId;
          }

          this.cdRef.markForCheck();
        }
      });

    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('RaceFinalComponent:ngOnInit', false);
      return;
    }
  }

  ngAfterViewChecked(): void {
    this.setResultsScroll();
  }

  ngOnDestroy(): void {
    this.getIsConnectedInternetSubs?.unsubscribe();
  }

  setResultsScroll(): void {
    if (this.setResultsScrollTimeout) clearTimeout(this.setResultsScrollTimeout);
    this.setResultsScrollTimeout = setTimeout(() => {
      if (this.raceFinalResultsRef?.nativeElement) {

        const headerSectionHeight = (document.querySelector(".header-section") as any)?.offsetHeight;
        const eventtrackSelectionSectionHeight = (document.querySelector(".eventtrack-selection-section") as any)?.offsetHeight;
        const raceSectionHeight = (document.querySelector(".race-section") as any)?.offsetHeight;
        const raceSelectionSectionHeight = (document.querySelector(".race-selection-section") as any)?.offsetHeight;
        const appSectionHeight = (document.querySelector(".app-section") as any)?.offsetHeight;
        const footerContentHeight = (document.querySelector(".footer-content") as any)?.offsetHeight;
        const closeRaceSectionHeight = (document.querySelector(".close-race-section") as any)?.offsetHeight || 0;
        const matrixButton = 34;

        this.renderer.setStyle(
          this.raceFinalResultsRef?.nativeElement,
          'height',
          String(
            appSectionHeight-
            (headerSectionHeight+eventtrackSelectionSectionHeight+raceSectionHeight+raceSelectionSectionHeight)-
            footerContentHeight-closeRaceSectionHeight-8-
            matrixButton
          )+'px'
        );
      }
    }, 300);
  }

  hasPermission(): boolean {
    const isAllowed = this.permissionService.isAllowed() || false;

    if (isAllowed) {
      return true;
    }

    if (navigator.onLine) this.router.navigate(['/login']);
    return false;
  }

  async openMatrix(): Promise<void> {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('RaceFinalComponent:openMatrix', false);
      return;
    }

    // if (!this.hasPermission()) return;
    if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
      this.router.navigate(['login']);

      return;
    }

    this.wagerFacade.updateTriggerMatrixModal('RaceFinalComponent:openMatrix');
  }

  async replayVideo(): Promise<void> {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('RaceFinalComponent:replayVideo', false);
      return;
    }

    if (!this.hasPermission()) {

      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.router.navigate(['login']);
      }
      else {
        this.showAlertInfo('You are not an Identified User.');
      }

      return;
    }

    this.wagerFacade.setSelectedReplayInfo('', '', '', '');

    this.wagerFacade.getEventRaceVideoReplay(
      'RaceFinalComponent:replayVideo',
      {
        toteEventId: this.eventId,
        toteRaceId: this.raceId,
        raceDay: this.toDate(
          new Date(this.selectedEvent?.ToteResponse?.EventDetail?.EventInfo),
          2
        ).toString(),
    // raceDay: this.toDate(new Date(this.selectedEvent?.ToteResponse?.EventDetail?.EventInfo), 2),
        width: 720,
        height: 576
      }
    );

    // const modal = await this.modalCtrl.create({
    //   component: PlayVideoComponent,
    //   cssClass: 'play-video'
    // });
    // modal.present();

    // const { role } = await modal.onWillDismiss();
  }

  toDate(date: Date, format: number = 1): string {
    const month = _.padStart(String(date.getMonth()+1), 2, '0');
    const day = _.padStart(String(date.getDate()), 2, '0');

    if(format == 2) return month + "-" + day + "-" + date.getFullYear();
    else return date.getFullYear() + "-" + month + "-" + day;
  }

  async showAlertInfo(message: string): Promise<void> {
    this.showAlertInfoHandler = await this.alertCtrl.create({
      header: 'Info',
      message,
      buttons: [
        {
          text: 'Close',
          handler: () => {
          }
        }
      ]
    });

    await this.showAlertInfoHandler.present();
  }

  results(): void {
    if (!this.raceId || !this.eventId || !this.runId) return;

    // get race result
    this.wagerFacade.getRaceResultAPI({
        raceId: this.raceId,
        eventId: this.eventId,
        runId: this.runId
      },
      'RaceFinalComponent:ngOnChanges'
    );

    // disconnect previous observer connection
    this.getRaceResultSubs?.unsubscribe();
    this.getRaceResultSubs = undefined;

    // handle the response from API request
    this.getRaceResultSubs = this.wagerFacade.getRaceResult$()
      ?.subscribe((results?: ISelectedRaceRaceResult) => {

        if (
          (results?.ToteResponse?.RaceId !== this.raceId)
          || (results?.ToteResponse?.EventId !== this.eventId)
        ) return;

        if (this.raceResults) this.isFetchingData = false;
        else this.isFetchingData = Boolean(results?.isGettingResult);

        if (results?.isSuccessResult == true) {
          // success

          this.raceResults = results?.ToteResponse || undefined;

          if (this.raceResults) {
            this.poolsTotal = results?.PoolsTotal;
            this.totalHandle = results?.totalHandle;
            this.personalStatistic = results?.personalStatistic;

            // for personalStatistic calculations
            this.ticketFacade.getCountersAPI(String(this.runId), 'RaceFinalComponent:ngOnChanges');
          }

          this.isFetchingData = false;

          // disconnect previous observer connection
          // means it is resolved
          this.getRaceResultSubs?.unsubscribe();
          this.getRaceResultSubs = undefined;
        }
        else if (results?.isSuccessResult === false) {
          // error

          this.raceResults = undefined;
          this.isFetchingData = false;

          // disconnect previous observer connection
          // means it is resolved
          this.getRaceResultSubs?.unsubscribe();
          this.getRaceResultSubs = undefined;
        }

        this.cdRef.markForCheck();
      });
  }
}
